<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import {
    isStakeholderClient,
    isStakeholderVendor,
    type StakeholderType
  } from '@gat-ship/types'
  import {
    cn,
    Modal,
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarHeader,
    SidebarInset,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider
  } from '@gat-ship/vue-components'
  import { IconClose, IconMaximize, IconMinimize } from '@gat-ship/icons'
  import { useStakeholderContext } from '../../composables/useStakeholder'
  import { useStakeholderDetail } from '../providers/stakeholder-detail-context'
  import {
    getStakeholderDetailSidebarItems,
    type StakeholderDetailSection
  } from '../detail-modal/stakeholder-detail-modal-sections'
  import StakeholderSectionGeneralInfo from '../groups/modal/StakeholderSectionGeneralInfo.vue'
  import StakeholderSectionInternalIdentificationNumbers from '../groups/modal/StakeholderSectionInternalIdentificationNumbers.vue'
  import StakeholderSectionOfficialBusinessNumbers from '../groups/modal/StakeholderSectionOfficialBusinessNumbers.vue'
  import StakeholderSectionPostalAddress from '../groups/modal/StakeholderSectionPostalAddress.vue'
  import StakeholderSectionVisitAddress from '../groups/modal/StakeholderSectionVisitAddress.vue'
  import StakeholderSectionBillingDetails from '../groups/modal/StakeholderSectionBillingDetails.vue'
  import StakeholderSectionBillingAddress from '../groups/modal/StakeholderSectionBillingAddress.vue'
  import StakeholderSectionRemove from '../groups/modal/StakeholderSectionRemove.vue'
  import StakeholderSectionRestore from '../groups/modal/StakeholderSectionRestore.vue'
  import { ContactsProvider } from '@gat-ship/stakeholder-contacts'
  import { ContactPersonsList } from '@gat-ship/stakeholder-contacts'
  import StakeholderSelect, {
    type SelectStakeholderItem
  } from '../inputs/StakeholderSelect.vue'
  import { useStakeholderRecentlyVisited } from '@gat-ship/stakeholder-search'

  const props = defineProps<{
    stakeholderType: StakeholderType
  }>()

  const open = defineModel<boolean>('open', { required: true })
  const activeSection = defineModel<StakeholderDetailSection>('section', {
    required: true
  })

  const emit = defineEmits<{
    (e: 'select', item: SelectStakeholderItem): void
  }>()

  const service = useStakeholderContext()
  const { dynamicFields } = useStakeholderDetail()
  const { isSystemFieldVisible } = dynamicFields

  const stakeholder = computed(() => service?.stakeholder.value)

  const sidebarItems = computed(() =>
    getStakeholderDetailSidebarItems(props.stakeholderType)
  )

  const headerTitle = computed(() => stakeholder.value?.name ?? 'Stakeholder')

  const { recentlyVisited, fetchRecentlyVisited } =
    useStakeholderRecentlyVisited()

  // The recently-visited list is lazily hydrated from user-state on first read.
  // The layout hydrates it as a side effect of recording a visit; the modal
  // never records, so hydrate it ourselves when opened (no-op once fetched).
  watch(
    open,
    (isOpen) => {
      if (isOpen) fetchRecentlyVisited()
    },
    { immediate: true }
  )

  // Feeds the sidebar switcher. Recently-visited stakeholders plus the current
  // one (prepended if absent) so the select can always render the active
  // stakeholder in its trigger.
  const selectItems = computed<SelectStakeholderItem[]>(() => {
    const items = recentlyVisited.value.map((i) => ({
      id: i.id,
      name: i.text,
      stakeholderType: i.type
    }))
    const current = stakeholder.value
    if (current && !items.some((i) => i.id === current.id)) {
      items.unshift({
        id: current.id,
        name: current.name,
        stakeholderType: current.stakeholderType
      })
    }
    return items
  })

  // Viewport-filling mode, toggled from the header next to the close button.
  // Kept across open/close within the session (the modal root stays mounted),
  // but not persisted. Mirrors AdministrationModal.
  const isFullScreen = ref(false)

  // Overrides on top of sidebarMenuButtonVariants to match the user-settings
  // modal styling (auto height, muted inactive text). ring-inset: the sidebar
  // content scrolls, which would clip a ring drawn outside the button bounds.
  const menuButtonClass =
    'text-muted-foreground hover:bg-accent/50 hover:text-foreground data-[active=true]:hover:bg-accent data-[active=true]:hover:text-accent-foreground h-auto cursor-pointer px-3 py-2 focus-visible:ring-inset'

  /** Mobile tab-bar item classes. */
  function tabClass(value: StakeholderDetailSection) {
    return cn(
      'flex cursor-pointer items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors',
      activeSection.value === value
        ? 'border-primary text-foreground font-medium'
        : 'text-muted-foreground hover:text-foreground hover:border-border border-transparent'
    )
  }
</script>

<template>
  <Modal
    v-model:open="open"
    type="dialog"
    :max-width="1000"
    full-screen-mobile
    hide-close-button
    :full-screen="isFullScreen"
  >
    <SidebarProvider
      :class="
        cn(
          'h-full min-h-0 w-full overflow-hidden',
          isFullScreen ? 'md:h-full' : 'md:h-[min(800px,85vh)] md:rounded-lg'
        )
      "
    >
      <ResizablePanelGroup
        direction="horizontal"
        auto-save-id="stakeholder-modal-sidebar"
      >
        <ResizablePanel
          class="hidden md:block"
          :default-size="22"
          :min-size="18"
          :max-size="40"
        >
          <Sidebar
            collapsible="none"
            class="bg-muted/50 hidden h-full w-full p-3 dark:bg-neutral-800/50 md:flex"
          >
            <SidebarHeader class="px-1 py-1">
              <StakeholderSelect
                :model-value="stakeholder?.id"
                :items="selectItems"
                :clearable="false"
                :enable-search="true"
                :enable-recently-visited="true"
                required
                @select="emit('select', $event)"
              />
            </SidebarHeader>
            <SidebarContent class="gap-1">
              <SidebarGroup class="p-0">
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem
                      v-for="item in sidebarItems"
                      :key="item.key"
                    >
                      <SidebarMenuButton
                        :is-active="activeSection === item.key"
                        :class="menuButtonClass"
                        @click="activeSection = item.key"
                      >
                        <component :is="item.icon" />
                        <span>{{ item.label }}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>
          </Sidebar>
        </ResizablePanel>
        <ResizableHandle class="hidden md:flex" />
        <ResizablePanel class="flex flex-col" :default-size="78">
          <SidebarInset class="overflow-hidden">
            <div class="relative z-10 shrink-0">
              <div class="bg-background flex items-center px-3 py-1.5">
                <div class="min-w-0 flex-1">
                  <span
                    class="text-foreground truncate pl-1 text-sm font-semibold md:hidden"
                  >
                    {{ headerTitle }}
                  </span>
                </div>
                <button
                  type="button"
                  class="text-muted-foreground hover:text-foreground hover:bg-accent hidden shrink-0 cursor-pointer rounded-md p-1.5 transition-colors md:block"
                  @click="isFullScreen = !isFullScreen"
                >
                  <IconMinimize v-if="isFullScreen" class="size-4" />
                  <IconMaximize v-else class="size-4" />
                  <span class="sr-only">
                    {{ isFullScreen ? 'Exit full screen' : 'Full screen' }}
                  </span>
                </button>
                <button
                  type="button"
                  class="text-muted-foreground hover:text-foreground hover:bg-accent shrink-0 cursor-pointer rounded-md p-1.5 transition-colors"
                  @click="open = false"
                >
                  <IconClose class="size-4" />
                  <span class="sr-only">Close</span>
                </button>
              </div>
              <nav
                class="border-border bg-background flex shrink-0 gap-1 overflow-x-auto border-b px-3 md:hidden"
              >
                <button
                  v-for="item in sidebarItems"
                  :key="item.key"
                  type="button"
                  :class="tabClass(item.key)"
                  @click="activeSection = item.key"
                >
                  <component :is="item.icon" class="size-4" />
                  <span>{{ item.label }}</span>
                </button>
              </nav>
              <div
                class="bg-background pointer-events-none h-3"
                style="
                  mask-image: linear-gradient(to bottom, black, transparent);
                "
              />
            </div>
            <div class="-mt-3 flex flex-1 flex-col gap-6 overflow-y-auto p-5">
              <template v-if="activeSection === 'general'">
                <StakeholderSectionGeneralInfo />
                <StakeholderSectionInternalIdentificationNumbers
                  v-if="
                    stakeholder &&
                    ((isStakeholderClient(stakeholder) &&
                      isSystemFieldVisible('clientNumber')) ||
                      (isStakeholderVendor(stakeholder) &&
                        isSystemFieldVisible('vendorNumber')))
                  "
                />
                <StakeholderSectionOfficialBusinessNumbers
                  v-if="
                    stakeholder &&
                    (isStakeholderVendor(stakeholder) ||
                      isStakeholderClient(stakeholder)) &&
                    (isSystemFieldVisible('taxIdentificationNumber') ||
                      isSystemFieldVisible('eoriNumber'))
                  "
                />
                <StakeholderSectionPostalAddress
                  v-if="isSystemFieldVisible('postalAddress')"
                />
                <StakeholderSectionVisitAddress
                  v-if="isSystemFieldVisible('visitAddress')"
                />
              </template>
              <template v-else-if="activeSection === 'billing'">
                <StakeholderSectionBillingDetails />
                <StakeholderSectionBillingAddress />
              </template>
              <template v-else-if="activeSection === 'contacts'">
                <ContactsProvider
                  v-if="stakeholder"
                  :stakeholder-id="stakeholder.id"
                >
                  <ContactPersonsList />
                </ContactsProvider>
              </template>
              <template v-else-if="activeSection === 'danger-zone'">
                <StakeholderSectionRemove />
                <StakeholderSectionRestore />
              </template>
            </div>
          </SidebarInset>
        </ResizablePanel>
      </ResizablePanelGroup>
    </SidebarProvider>
  </Modal>
</template>
